/**
 * @author      Wes Garland, wes@page.ca
 * @date        Dec 2012, Jun 2018
 */
'use strict';

var debugBuild = Boolean(globalThis.hasOwnProperty('dcpConfig') && dcpConfig.build === 'debug');

const LISTENERS = '__listeners';
const caseSensitiveEventNames = false; /* default false - DOM-level-2 compliant */

exports.breakpointEvents = {};

/** An EventEmitter class which is API-compatible with Node's EventEmitter class. 
 * This class essentially registers and executes callbacks by attaching them to strings.
 * There is no standard `event` object at the moment, so callbacks simply are passed whatever args the 
 * registered event passes.
 * @memberof module:dcp/dcp-events
 * @access public
 */
class EventEmitter {
  constructor(debugLabel) {
    this.debugLabel = debugLabel || "";
    if (typeof require === 'function') {
      this.debug = (require('dcp/debugging').scope('event-emitter'))(this.debugLabel || 'all');
    }
  }

  /** Add an event listener. 
   *  @param {string}   eventName    The name of the event to capture, or true to capture all events
   *  @param {function} eventHandler The function to invoke when the eventHandler is invoked.  The arguments
   *                                 to the function are specified by the code emitting the event, however
   *                                 a final argument, eventName, is appended to event handlers invoked via
   *                                 catch-alls.        
   *  @access public
   */
  addListener(eventName, eventHandler) {

    if (!this.hasOwnProperty(LISTENERS))
    {
      this[LISTENERS] = {}; /* must not be moved to ctor! */
      if (debugBuild && !this.breakpointEvents)
        this.breakpointEvents = {};
    }
    
    eventName = sanitizeEventName(eventName);
    if (!this[LISTENERS].hasOwnProperty(eventName))
      this[LISTENERS][eventName] = [];

    if (typeof eventHandler !== "function")
      throw new Error("eventHandler is not a function");

    this[LISTENERS][eventName].push(eventHandler);
    this.emit("newListener", eventName, eventHandler);
  }

  /**
   * Alias of {@link module:dcp/dcp-events.EventEmitter#addListener|addListener}.
   * @access public
   */
  on(eventName, eventHandler) {
    this.addListener(eventName, eventHandler);
  }

  /**
   * Alias of {@link module:dcp/dcp-events.EventEmitter#addListener|addListener}.
   * @access public
   */
  addEventListener(eventName, eventHandler) {
    this.addListener(eventName, eventHandler);
  }

  /**
   * Attach a callback (eventHandler) to a named event. It will be called once when this event emitter
   * emits that event and then removed.
   * @param {string} eventName Name of event
   * @param {function} eventHandler Function to call
   * @access public
   */
  once(eventName, eventHandler) {
    const that = this;
    function onceWrapper() {
      eventHandler.apply(eventHandler, arguments);
      that.removeListener(eventName, onceWrapper);
    }
    this.addListener(eventName, onceWrapper);
    return onceWrapper;
  }

  /**
   * Remove a listener (`eventHandler`) attached to a named event. It must
   * be the same `eventHandler` instance that was originally attached.
   * @param {string} eventName Name of event
   * @param {function} eventHandler Function to call
   * @access public
   */
  removeListener(eventName, eventHandler) {
    var i;

    eventName = sanitizeEventName(eventName);
    if (!this[LISTENERS] || !this[LISTENERS].hasOwnProperty(eventName))
      return;

    i = this[LISTENERS][eventName].indexOf(eventHandler);
    if (i != -1)
    {
      this[LISTENERS][eventName].splice(i, 1);
      if (this[LISTENERS][eventName].length === 0)
        delete this[LISTENERS][eventName];
    }
  }

  /**
   * Alias of {@link module:dcp/dcp-events.EventEmitter#removeListener|removeListener}.
   * @access public
   */
  off(eventName, eventHandler) {
    this.removeListener(eventName, eventHandler)
  }

  /**
   * Returns an array listing the events for which the emitter has registered listeners.
   * The values in the array are strings.
   */
  eventNames()
  {
    return Object.keys(this[LISTENERS]);
  }

  /**
   * Alias of {@link module:dcp/dcp-events.EventEmitter#removeListener|removeListener}.
   * @access public
   */
  removeEventListener(eventName, eventHandler) {
    this.removeListener(eventName, eventHandler)
  }

  removeAllListeners(eventName) {
    if (typeof eventName !== 'undefined') {
       eventName = sanitizeEventName(eventName);
      if (this[LISTENERS])
        delete this[LISTENERS][eventName];
    } else {
      this[LISTENERS] = {};
    }
  }

  listeners(eventName) {
    if (!this[LISTENERS])
      return [];
    
    eventName = sanitizeEventName(eventName);
    if (!this[LISTENERS].hasOwnProperty(eventName))
      this[LISTENERS][eventName] = [];

    return this[LISTENERS][eventName];
  }

  setMaxListeners(number) {
    throw new Error("not implemented");
  }

  listenerCount(eventName) {
    if (!this[LISTENERS])
      return 0;
    
    eventName = sanitizeEventName(eventName);
    return this[LISTENERS][eventName]? this[LISTENERS][eventName].length : 0;
  }

  /** Event emitter. All interfaces which emit events eventually fall through to this function. 
   *  This function invokes event listeners in the correct context for the passed eventName, as
   *  well as any catch-all (true) listeners.
   *
   *  @param      _eventName      The name of the event to emit.
   *  @param      args            An array (not array-like) object containing the arguments 
   *                              to pass to the listener.
   */
  vemit(_eventName, args)
  {
    var i;
    const eventName = sanitizeEventName(_eventName);

    if (!this[LISTENERS] || !(this[LISTENERS].hasOwnProperty(eventName) || this[LISTENERS].hasOwnProperty(true)))
    {
      if (this.debug && _eventName !== 'newListener')
        console.debug("Not firing " + this.debugLabel + "::" + _eventName + " -- no listeners");
      return;
    }

    if (this.debug)
      console.debug("Firing " + this.debugLabel + "::" + _eventName);

    if (this[LISTENERS].hasOwnProperty(eventName))
    {
      for (i=0; i < this[LISTENERS][eventName].length; i++)
      {
        const realEventHandler = this[LISTENERS][eventName][i];
        const that = this;
        let eventHandler = !debugBuild ? realEventHandler : function eventHandlerWrapper() {
          if (exports.breakpointEvents[eventName] || that.breakpointEvents[eventName])
            debugger; // allow-debugger
          return realEventHandler.apply(that, arguments);
        };
        this.runSoon(this, eventHandler, args);
      }
    }
    
    if (typeof this[LISTENERS][true] !== "undefined")
    {
      for (i=0; i < this[LISTENERS][true].length; i++)
        this.runSoon(this, this[LISTENERS][true][i], args.concat(eventName));
    }
  }

  /** Emit an event. 
   *
   *  @param {string} eventName The event to emit
   *  @param ... Any other arguments passed will be passed exactly to the listener.
   *  @access public
   */
  emit(eventName /* ... */) {
    var args;

    args = Array.prototype.slice.call(arguments);
    args.shift();

    return this.vemit(eventName, args);
  }

  /** Proxy all of the events from another EventEmitter or DOM EventTarget and 
   *  re-emit them as though they originated from this EventEmitter.
   * 
   *  @param      element         The EventEmitter / EventTarget whose events we are to re-emit
   *  @param      eventName ...   One or more arguments which are strings that contain the names of
   *                              the events to re-emit.
   */
  proxy(element, eventName /*, eventName... */) {
    var i, fun;
    var _this = this;

    for (i=1; i < arguments.length; i++) {
      fun = (function events$$EventEmitter$proxy$fun(thisEventName) {
        sanitizeEventName(thisEventName);
        return (function events$$EventEmitter$proxy$fun$bound() {
          if (typeof arguments[0] === 'object')
            arguments[0].proxyType = thisEventName;
          this.vemit(thisEventName, arguments) 
        }).bind(_this);
      })(arguments[i]);
      element.addEventListener(arguments[i], fun);
    }
  }

  /** Proxy an events from another EventEmitter or DOM EventTarget and 
   *  re-emit them as though they originated from this EventEmitter, under a new event type.
   * 
   *  @param      element                 The EventEmitter / EventTarget whose events we are to re-emit
   *  @param      captureEventName        The name of the event to re-emit, or true to re-emit all events
   *  @param      newEventName            The event type of the new event when eventName is not true; when
   *                                      eventName is true, it is used as a prefix prepended to the real
   *                                      event name (ev.type)
   *
   *  *note* -    Catch-all proxies are only supported on instances of events$$EventEmitter. That
   *              functionality is not supported by DOM events.
   */
  proxyAs(element, captureEventName, emitterEventName) {
    var fun;
    captureEventName = sanitizeEventName(captureEventName);
    emitterEventName = sanitizeEventName(emitterEventName);
    
    if (element === this && captureEventName === true)
      throw new Error("Infinite recursion is not supported");

    fun = (function events$$EventEmitter$proxyAs$eventHandler(ev) {
      var args = Array.prototype.slice.call(arguments);

      if (typeof ev === 'object' && ev.hasOwnProperty('type'))
        ev.proxyType = ev.type;

      if (captureEventName === true)
        this.vemit(emitterEventName + args.pop(), args);
      else
        this.vemit(emitterEventName, arguments);
    }).bind(this);

    element.addEventListener(captureEventName, fun);
  }

  /** Run this supplied function ASAP in the reactor loop, with the provided 'this' and arguments.
   *  @param      _this   [optional]      An object to use apply as 'this' when invoking fn
   *  @param      fn                      The function to invoke
   *  @param      args                    Array of arguments passed to fn during invocation
   */
  runSoon(_this, fn, args) {
    var callback;

    if (typeof _this === "function" && typeof args === 'undefined') {
      args = fn;
      fn = _this;
      _this = null;
    }

    if (typeof fn !== "function")
      throw new Error("fn is not a function");
    
    if (typeof args === 'object')
      callback = function runSoon_inner() { return fn.apply(_this, args) };
    else
      callback = function runSoon_inner() { return fn.apply(_this, []) };
    
    return this.doCallback(callback);
  }

  get doCallback() { 
    return typeof setImmediate === 'function' ? setImmediate : (cb) => { setTimeout(cb, 0) };
  }

  /** Debug interface - not stable */
  dumpKnownEvents() {
    var eventName, i;
    var list = [];

    if (!this[LISTENERS] || !this[LISTENERS].length)
      return list;

    for (eventName in this[LISTENERS])
      list.push(eventName);
    
    list.sort();
    for (i=0; i < list.length; i++) {
      if (list[i] == list[i+1])
        list.splice(i+1,1);
    }

    return list;
  }

  breakOn(eventName)
  {
    if (!debugBuild)
      throw new Error('breakOn feature not available in release builds');
    
    eventName = sanitizeEventName(eventName);
    if (!this.breakpointEvents)
      this.breakpointEvents = {};
    this.breakpointEvents[eventName] = true;
  }
}

function sanitizeEventName(eventName)
{
  if (!caseSensitiveEventNames)
    eventName = String(eventName).toLowerCase();
  return eventName;
}

exports.EventEmitter = EventEmitter;
exports.caseSensitiveEventNames = caseSensitiveEventNames;
exports.sanitizeEventName = sanitizeEventName;
